import { useEffect, useState } from "react";
import NewSelectInput from "../../../components/NewSelectInput";
import NormalSearch from "../../../components/common/NormalSearch";
import {
  IconTruck,
  IconCalendar,
  IconClipboardText,
} from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import { Control, UseFormClearErrors } from "react-hook-form";
import TextareaField from "../../../components/common/TextareaField";
import {
  useFetchAllPickupLocationData,
  useFetchOrderShipmentsData,
} from "../../../apis/pickupApis";
import Pagination from "../../../components/common/Pagination";
import ListView from "../../../components/common/ListView";

const vehicleTypes = [
  { id: 1, value: "car", text: "Car" },
  { id: 2, value: "van", text: "Van" },
  { id: 3, value: "dokker", text: "Dokker" },
];

const RequestTypeData = [
  { id: 1, value: "PICKUP", text: "Pickup Request" },
  { id: 2, value: "DROPOFF", text: "Drop off Request" },
];

type Props = {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors?: UseFormClearErrors<any>;
  control: Control<any>;
  reset?: any;
  setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>;
  selectedOrders: string[];
  pickupStep?: number;
};

const SectionHeader = ({
  title,
}: {
  icon?: any;
  title: string;
}) => (
  <div className="mb-2">
    <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">
      {title}
    </p>
  </div>
);

const GeneratePickupModal = ({
  register,
  errors,
  setValue,
  clearErrors,
  watch,
  selectedOrders,
  setSelectedOrders,
  pickupStep,
}: Props) => {
  const [searchFor, setSearchFor] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit] = useState(10);

  const { data: allPickupLocationData } = useFetchAllPickupLocationData();
  const { data: orderShipmentsData } = useFetchOrderShipmentsData(
    pageNo,
    limit,
    searchFor
  );

  const shipments = orderShipmentsData?.orders || [];
  const total = orderShipmentsData?.total || 0;

  useEffect(() => {
    setValue("requestType", "PICKUP");
  }, []);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = shipments.map((item: any) => String(item.id));
      setSelectedOrders(allIds);
      setValue("shipmentsId", allIds);
    } else {
      setSelectedOrders([]);
      setValue("shipmentsId", []);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    let next: string[];
    if (checked) {
      next = [...selectedOrders, id];
    } else {
      next = selectedOrders.filter((item) => item !== id);
    }
    setSelectedOrders(next);
    setValue("shipmentsId", next);
  };

  return (
    <div className="w-full py-4 px-6 space-y-6 bg-transparent">
      <div className="space-y-6">
        {pickupStep === 2 && (
          <>
            {/* Section 1: Pickup Details */}
            <div className="space-y-3">
              <SectionHeader icon={IconTruck} title="Pickup Details" />
              
              <div className="flex flex-col gap-4">
                 <NewSelectInput
                  label="Select Request Type"
                  name="requestType"
                  value={watch("requestType")}
                  clearErrors={clearErrors}
                  register={register}
                  error={errors?.requestType}
                  validation={{ required: "Request Type is required" }}
                  onChange={(val) => {
                    setValue("requestType", val);
                  }}
                  selectOptions={RequestTypeData}
                  className="flex items-center justify-between"
                  labelClass="w-full"
                />

                <NewSelectInput
                  label="Select Vehicle Type"
                  name="vehicleType"
                  value={watch("vehicleType")}
                  clearErrors={clearErrors}
                  register={register}
                  error={errors?.vehicleType}
                  validation={{ required: "Vehicle Type is required" }}
                  onChange={(val) => {
                    setValue("vehicleType", val);
                  }}
                  selectOptions={vehicleTypes}
                  className="flex items-center justify-between"
                  labelClass="w-full"
                />

                <NewSelectInput
                  label="Select Location"
                  name="locationId"
                  value={watch("locationId")}
                  clearErrors={clearErrors}
                  register={register}
                  error={errors?.locationId}
                  validation={{ required: "Location is required" }}
                  onChange={(val) => {
                    setValue("locationId", val);
                  }}
                  selectOptions={
                    allPickupLocationData?.map((loc: any) => ({
                      id: String(loc.id),
                      value: String(loc.id),
                      text: loc.name || loc.full_address,
                    })) || []
                  }
                  className="flex items-center justify-between"
                  labelClass="w-full"
                />

                <NewinputField
                  title="Scheduled Date & Time"
                  type="datetime-local"
                  name="pickupDateTime"
                  register={register}
                  error={errors?.pickupDateTime}
                  validation={{ required: "Scheduled Date & Time is required" }}
                  className="flex items-center justify-between"
                  labelClass="w-full"
                />
              </div>
            </div>

            {/* Section 2: Instructions */}
            <div className="space-y-3">
              <SectionHeader icon={IconClipboardText} title="Instructions" />
              
              <TextareaField
                name="description"
                label="Additional instructions (optional)"
                placeholder="Type any instructions here..."
                register={register}
              />
            </div>
          </>
        )}

        {pickupStep === 1 && (
          /* Section 3: Select Shipments */
          <div className="space-y-3">
            <SectionHeader icon={IconCalendar} title="Select Shipments" />

            <div className="flex items-center justify-between gap-3 mb-2">
              <NormalSearch
                value={searchFor}
                onChange={(e) => {
                  setSearchFor(e.target.value);
                  setPageNo(1);
                }}
                name="Search order..."
              />
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[300px] overflow-y-auto">
              <ListView
                mailDivClassName="p-0 rounded-2xl bg-transparent"
                thClassName="bg-transparent"
                tbodyClassName="bg-transparent"
                data={shipments}
                columns={[
                  {
                    label: (
                      <input
                        type="checkbox"
                        checked={
                          shipments.length > 0 &&
                          shipments.every((item: any) =>
                            selectedOrders.includes(String(item.id))
                          )
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="accent-[#FBD037]"
                      />
                    ),
                    render: (row: any) => (
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(String(row.id))}
                        onChange={(e) =>
                          handleSelectOne(String(row.id), e.target.checked)
                        }
                        className="accent-[#FBD037]"
                      />
                    ),
                  },
                  { label: "AWB Number", render: (row: any) => row.awbNumber },
                  { label: "Origin / Dest", render: (row: any) => `${row.originCityName || "—"} → ${row.destinationCityName || "—"}` },
                ]}
              />
            </div>

            {total > limit && (
              <div className="pt-2">
                <Pagination
                  currentPage={pageNo}
                  totalPages={Math.ceil(total / limit)}
                  fetchData={(page: number) => setPageNo(page)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratePickupModal;
